import React, { ReactNode } from "react";
import { commonModels } from '@gatherstar/models'
import dayjs from "dayjs";

export type ColumnDisplayType = 'text' | 'int' | 'currency' | 'percent' | 'ms';

export interface ColumnConfig {
  title: string;
  type: ColumnDisplayType;
  width?: number;
  render?: (text: ReactNode, record: any, index: number, action: any) => ReactNode | ReactNode[]
  renderText?: (text: any, record: any) => string;
  fixed?: 'left' | 'right';
  ellipsis?: boolean | { showTitle?: boolean };
}

const { deviceType } = commonModels
/**
 * 字段元数据配置表
 * 集中管理 Title, Width, Type
 */
export const FIELD_METADATA: Record<string, ColumnConfig> = {
  // --- 基础信息/时间/文本 ---
  dt: { title: '日期', type: 'text', width: 120 },
  date: {
    title: '日期',
    type: 'text',
    width: 120,
    fixed: 'left',
    renderText: (text: number, record) => dayjs(text).format('YYYY-MM-DD') || ''
  },
  hour: {
    title: '小时',
    type: 'text',
    width: 120,
    fixed: 'left',
    renderText: (text: number, record) => dayjs(text).format('YYYY-MM-DD HH:mm') || ''
  },
  week: {
    title: '周',
    type: 'text',
    width: 280,
    fixed: 'left',
    renderText: (text: number, record) => {
      const d = dayjs(text);
      if (!d.isValid()) return '-';
      const weekStart = d.startOf('week').format('YYYY-MM-DD');
      const weekEnd = d.endOf('week').format('YYYY-MM-DD');
      return `${d.year()}年-${d.week()}周(${weekStart}~${weekEnd})`;
    }
  },
  month: {
    title: '月份',
    type: 'text',
    width: 120,
    fixed: 'left',
    renderText: (text: number, record) => dayjs(text).format('YYYY-MM') || ''
  },
  id: { title: '产品ID', type: 'text', width: 120 },
  createTime: { title: '发生日期', type: 'text', width: 200 },
  productName: { title: '产品名称', type: 'text', width: 220, ellipsis: true },
  businessTypeDesc: { title: '类型', type: 'text', width: 120 },
  description: { title: '备注', type: 'text', width: 160 },
  business: { title: '商务经理', type: 'text', width: 120 },
  dspId: { title: 'DSP ID', type: 'text', width: 120 },
  dspName: { title: 'DSP名称', type: 'text', width: 150 },
  sspId: { title: 'SSP ID', type: 'text', width: 140 },
  sspName: { title: 'SSP名称', type: 'text', width: 160 },
  publisherId: { title: 'Publisher ID', type: 'text', width: 150 },
  appId: { title: '应用ID', type: 'text', width: 140 },
  adType: { title: '广告类型', type: 'text', width: 120 },
  adSize: { title: '广告尺寸', type: 'text', width: 130 },
  country: { title: '国家', type: 'text', width: 100 },
  appName: { title: '应用名称/ID', type: 'text', width: 150 },
  appPackage: { title: '应用包名/域名', type: 'text', width: 150 },
  adPositionName: { title: '广告位名称/ID', type: 'text', width: 150 },
  mediaId: { title: '媒体ID', type: 'text', width: 120 },
  mediaName: { title: '媒体名称', type: 'text', width: 150 },
  publisherName: { title: '媒体名称', type: 'text', width: 150 },
  app: { title: '应用', type: 'text', width: 120 },
  advertiserId: { title: '广告主ID', type: 'text', width: 120 },
  advertiserName: { title: '广告主名称', type: 'text', width: 150 },
  campaignId: { title: '系列ID', type: 'text', width: 120 },
  campaignName: {
    title: '系列名称', type: 'text', ellipsis: true,
    width: 220
  },
  adGroupId: { title: '广告组ID', type: 'text', width: 120 },
  adGroupName: { title: '广告组名称', type: 'text', width: 220, ellipsis: true },
  adId: { title: '广告ID', type: 'text', width: 120 },
  adName: { title: '广告名称', type: 'text', width: 220, ellipsis: true },
  os: { title: '操作系统', type: 'text', width: 120 },
  creativeName: { title: '创意名称', type: 'text', width: 280, ellipsis: true },
  creativeId: { title: '创意ID', type: 'text', width: 120 },
  materialName: { title: '素材名称', type: 'text', width: 280, ellipsis: true },
  materialId: { title: '素材ID', type: 'text', width: 120 },

  // --- 货币 (USD) ---
  totalCost: { title: '消耗(USD)', type: 'currency', width: 120 },
  cpm: { title: '千次展示成本(USD)', type: 'currency', width: 180 },
  avgCostPerClick: { title: '平均点击成本(USD)', type: 'currency', width: 180 },
  avgCostPerConv: { title: '平均转化成本(USD)', type: 'currency', width: 180 },
  adSpend: { title: '广告消耗(USD)', type: 'currency', width: 120 },
  mediaRevenue: { title: '媒体收入(USD)', type: 'currency', width: 120 },
  grossProfit: { title: '毛利(USD)', type: 'currency', width: 120 },
  ecpm: { title: 'eCPM', type: 'currency', width: 120 },
  ecpc: { title: 'eCPC', type: 'currency', width: 120 },
  spent: { title: '媒体成本(USD)', type: 'currency', width: 120 },
  // --- 资金变动 (USD) ---
  amount: { title: '金额(USD)', type: 'currency', width: 160 },
  balanceBefore: { title: '变动前余额（USD）', type: 'currency', width: 180 },
  balanceAfter: { title: '余额(USD)', type: 'currency', width: 180 },

  // --- 整数 ---
  impressions: { title: '曝光量', type: 'int', width: 120 },
  clicks: { title: '点击量', type: 'int', width: 120 },
  conversions: { title: '转化量', type: 'int', width: 100 },
  requests: { title: '请求量', type: 'int', width: 120 },
  validRequests: { title: '有效请求量', type: 'int', width: 120 },
  biddingParticipants: { title: '参竞量', type: 'int', width: 120 },
  noBidding: { title: '未竞量', type: 'int', width: 120 },
  wonBids: { title: '竞得量', type: 'int', width: 120 },
  invalidRequests: { title: '无效请求量', type: 'int', width: 120 },
  errors: { title: '错误量', type: 'int', width: 120 },
  skips: { title: '跳过量', type: 'int', width: 120 },
  timeouts: { title: '超时量', type: 'int', width: 120 },
  impressionCount: { title: '曝光量', type: 'int', width: 120 },
  clickCount: { title: '点击量', type: 'int', width: 120 },
  conversionCount: { title: '转化量', type: 'int', width: 120 },

  // --- 货币 (ADX大盘/数据中心) ---
  revenue: { title: '收入(USD)', type: 'currency', width: 130 },
  cost: { title: '成本(USD)', type: 'currency', width: 130 },
  spend: { title: '消耗(USD)', type: 'currency', width: 130 },
  sspYesterdayCost: { title: 'SSP上一日花费', type: 'currency', width: 130 },
  dspYesterdaySpend: { title: 'DSP上一日消耗', type: 'currency', width: 130 },
  yesterdayProfit: { title: '上一日毛利', type: 'currency', width: 120 },
  yesterdayProfitRate: { title: '上一日毛利率', type: 'percent', width: 120 },
  sspTodayCost: { title: 'SSP本日花费', type: 'currency', width: 120 },
  dspTodaySpend: { title: 'DSP本日消耗', type: 'currency', width: 120 },
  todayProfit: { title: '本日毛利', type: 'currency', width: 110 },
  todayProfitRate: { title: '本日毛利率', type: 'percent', width: 110 },
  profitChange: { title: '毛利变化', type: 'currency', width: 110 },
  profitRateChange: { title: '毛利率变化', type: 'percent', width: 110 },
  estimatedRevenue: { title: '预估收入(USD)', type: 'currency', width: 130 },
  ecpa: { title: 'eCPA', type: 'currency', width: 110 },

  // --- 整数 (千分位) ---
  requestCount: { title: '请求量', type: 'int', width: 120 },
  bidCount: { title: '参竞量', type: 'int', width: 120 },
  winCount: { title: '竞得量', type: 'int', width: 120 },
  errorCount: { title: '错误量', type: 'int', width: 120 },
  timeoutCount: { title: '超时量', type: 'int', width: 120 },
  sendableInventory: { title: '可发送库存', type: 'int', width: 130 },
  actualInventory: { title: '实际发送库存', type: 'int', width: 130 },

  // --- 百分比 (%) ---
  ctr: { title: '点击率', type: 'percent', width: 100 },
  cvr: { title: '转化率(CVR)', type: 'percent', width: 120 },
  grossProfitRate: { title: '毛利率', type: 'percent', width: 120 },
  clickThroughRate: { title: '点击率', type: 'percent', width: 120 },
  fillRate: { title: '填充率', type: 'percent', width: 120 },
  noBiddingRate: { title: '未竞率', type: 'percent', width: 120 },
  winRate: { title: '竞得率', type: 'percent', width: 120 },
  invalidRequestRate: { title: '无效请求率', type: 'percent', width: 120 },
  errorRate: { title: '错误率', type: 'percent', width: 120 },
  skipRate: { title: '跳过率', type: 'percent', width: 120 },
  timeoutRate: { title: '超时率', type: 'percent', width: 120 },
  impressionRate: { title: '曝光率', type: 'percent', width: 120 },
  grossProfitMargin: { title: '毛利率', type: 'percent', width: 110 },
  // --- 毫秒 ---
  averageRequestTime: { title: '平均请求时间(ms)', type: 'ms', width: 120 },

  deviceType: {
    title: '设备类型', type: 'text', width: 120
  },
};